import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import '../styles/Measurements.css';

const MEASUREMENT_FIELDS = [
  { key: 'chestAround', label: 'Chest Around', help: 'Measure around the fullest part of your chest' },
  { key: 'shoulderToHem', label: 'Shoulder to Hem', help: 'Measure from shoulder seam to bottom hem' },
  { key: 'sleeveLength', label: 'Sleeve Length', help: 'Measure from shoulder to cuff' },
  { key: 'waist', label: 'Waist', help: 'Measure around your natural waistline' },
  { key: 'hip', label: 'Hip', help: 'Measure around the fullest part of your hips' },
  { key: 'inseam', label: 'Inseam (Inside Leg)', help: 'Measure from the top of your inner thigh down to your ankle' },
  { key: 'height', label: 'Height', help: 'Your full height' },
];

const EMPTY = { chestAround: '', shoulderToHem: '', sleeveLength: '', waist: '', hip: '', inseam: '', height: '' };

function Measurements() {
  const navigate = useNavigate();
  const [unit, setUnit] = useState('cm');
  const [displayMeasurements, setDisplayMeasurements] = useState({ ...EMPTY });
  const [measurements, setMeasurements] = useState({ ...EMPTY });
  const [toast, setToast] = useState(null);

  const cmToInches = (cm) => (cm ? (parseFloat(cm) / 2.54).toFixed(2) : '');
  const inchesToCm = (inches) => (inches ? (parseFloat(inches) * 2.54).toFixed(2) : '');

  const convertMeasurements = useCallback((meas, targetUnit) => {
    const converted = {};
    Object.keys(meas).forEach(key => {
      converted[key] = meas[key]
        ? (targetUnit === 'inches' ? cmToInches(meas[key]) : meas[key])
        : '';
    });
    return converted;
  }, []);

  const handleUnitChange = (newUnit) => {
    setUnit(newUnit);
    setDisplayMeasurements(convertMeasurements(measurements, newUnit));
  };

  const handleMeasurementChange = (field, value) => {
    setDisplayMeasurements(prev => ({ ...prev, [field]: value }));
    const cmValue = unit === 'inches' ? inchesToCm(value) : value;
    setMeasurements(prev => ({ ...prev, [field]: cmValue }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }

    const fetchData = async () => {
      try {
        await apiFetch('/auth/verify');
        const data = await apiFetch('/measurements');
        if (data && data.length > 0) {
          // Merge over EMPTY so fields added after the data was saved
          // (e.g. inseam/height) stay controlled inputs rather than undefined
          const cmMeasurements = { ...EMPTY, ...data[0].measurements };
          setMeasurements(cmMeasurements);
          setDisplayMeasurements(convertMeasurements(cmMeasurements, unit));
        }
      } catch {
        localStorage.removeItem('token');
        navigate('/');
      }
    };
    fetchData();
  }, [unit, convertMeasurements, navigate]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiFetch('/measurements/save', {
        method: 'POST',
        body: JSON.stringify({
          garmentType: 'general',
          measurements,
          unit: 'cm',
        }),
      });
      showToast('Measurements saved successfully');
    } catch {
      showToast('Error saving measurements', 'error');
    }
  };

  return (
    <div className="measurements-page">
      <div className="measurements-container">
        <div className="measurements-header">
          <h1>My Measurements</h1>
          <p className="measurements-subtitle">
            Enter your body measurements below. These will be used to recommend your size across all stores.
          </p>
          <p className="measurements-accuracy-note">
            The more measurements you provide, the more accurate your size recommendations will be.
          </p>
        </div>

        <div className="unit-toggle">
          <button
            className={`unit-btn ${unit === 'cm' ? 'active' : ''}`}
            onClick={() => handleUnitChange('cm')}
          >
            cm
          </button>
          <button
            className={`unit-btn ${unit === 'inches' ? 'active' : ''}`}
            onClick={() => handleUnitChange('inches')}
          >
            inches
          </button>
        </div>

        <form onSubmit={handleSubmit} className="measurements-form">
          <div className="measurements-grid">
            {MEASUREMENT_FIELDS.map(({ key, label, help }) => (
              <div className="measurement-field" key={key}>
                <label htmlFor={key}>{label}</label>
                <div className="measurement-input-wrapper">
                  <input
                    type="number"
                    id={key}
                    step="0.1"
                    value={displayMeasurements[key]}
                    onChange={(e) => handleMeasurementChange(key, e.target.value)}
                    placeholder="—"
                  />
                  <span className="measurement-unit">{unit}</span>
                </div>
                <small className="measurement-help">{help}</small>
              </div>
            ))}
          </div>

          <button type="submit" className="btn btn-primary btn-lg save-btn">
            Save Measurements
          </button>
        </form>

        <div className="measurement-tips">
          <h3>Measurement Tips</h3>
          <ul>
            <li>Use a flexible measuring tape for accurate results</li>
            <li>Measure over light clothing or directly on skin</li>
            <li>Keep the tape parallel to the floor</li>
            <li>Don't pull the tape too tight — it should rest comfortably</li>
          </ul>
        </div>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default Measurements;
